import { call, put, takeEvery, takeLatest } from "redux-saga/effects";
import type { PayloadAction } from "@reduxjs/toolkit";
import request, { type RequestError } from "../../../utils/request";
import { API_ENDPOINT } from "../../../utils/constants";
import { handleAxiosError } from "../../../utils/APIValidations";
import { cleanValue } from "../../../utils/cleanValue";
import { formatUkDate, formatUkTimeOfDay } from "../../../utils/datetime";
import {
  loadCount,
  countLoaded,
  loadCountError,
  loadAppointments,
  appointmentsLoaded,
  loadAppointmentsError,
  loadAppointmentDetails,
  appointmentDetailsLoaded,
  loadAppointmentDetailsError,
} from "./slice";
import type {
  AllAppointmentsResponse,
  ApiAppointmentRecord,
  Appointment,
  AppointmentCount,
  AppointmentDetailsResponse,
  LoadAppointmentDetailsPayload,
  LoadAppointmentsPayload,
} from "./types";

function isNoData(e: unknown): boolean {
  return (e as RequestError)?.status === 404;
}

function mapRecord(r: ApiAppointmentRecord, index: number): Appointment {
  const id = cleanValue(r.call_id);
  return {
    key: id || `appt-${index}`,
    appointmentId: id,
    patientName: cleanValue(r.patient_name) || "Unknown",
    patientPhone: cleanValue(r.phone_number) || "—",
    service: cleanValue(r.service) || "—",
    dentist: cleanValue(r.dentist) || "—",
    status: cleanValue(r.status) || "—",
    date: formatUkDate(r.created_at),
    time: formatUkTimeOfDay(r.created_at),
    isDone: !!r.is_done,
  };
}

export function* fetchCount() {
  try {
    const response: AppointmentCount = yield call(
      request,
      `${API_ENDPOINT}dashboard/appointments/count`,
    );
    yield put(countLoaded(response));
  } catch (e) {
    if (isNoData(e)) {
      yield put(
        countLoaded({
          total_appointments: 0,
          scheduled_appointments: 0,
          unsuccessful_appointments: 0,
          cancelled_appointments: 0,
        }),
      );
      return;
    }
    yield call(handleAxiosError, e);
    yield put(loadCountError());
  }
}

export function* fetchAppointments({
  payload,
}: PayloadAction<LoadAppointmentsPayload | undefined>) {
  try {
    const params = new URLSearchParams({ page: "1", per_page: "1000" });
    if (payload?.startDate) params.set("start_date", payload.startDate);
    if (payload?.endDate) params.set("end_date", payload.endDate);

    const response: AllAppointmentsResponse = yield call(
      request,
      `${API_ENDPOINT}dashboard/all-appointments?${params.toString()}`,
    );
    yield put(appointmentsLoaded(response.records.map(mapRecord)));
  } catch (e) {
    if (isNoData(e)) {
      yield put(appointmentsLoaded([]));
      return;
    }
    yield call(handleAxiosError, e);
    yield put(loadAppointmentsError());
  }
}

export function* fetchAppointmentDetails({
  payload,
}: PayloadAction<LoadAppointmentDetailsPayload>) {
  try {
    const response: AppointmentDetailsResponse = yield call(
      request,
      `${API_ENDPOINT}dashboard/appointment-details?external_call_id=${encodeURIComponent(
        payload.appointmentId,
      )}`,
    );
    yield put(appointmentDetailsLoaded(response));
  } catch (e) {
    yield call(handleAxiosError, e);
    yield put(loadAppointmentDetailsError());
  }
}

export default function* saga() {
  yield takeLatest(loadCount.type, fetchCount);
  yield takeLatest(loadAppointments.type, fetchAppointments);
  yield takeEvery(loadAppointmentDetails.type, fetchAppointmentDetails);
}
